import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FormInput from "../Input/formInput";
import ProductMultiSelectionModal from "./productMultiSelectionModel";
import CustomModal from "./customModal";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";

const PurchaseOrderProductSelector = ({
  selectedProducts,
  setSelectedProducts,
  setLoading,
  compID,
  poId,
  onSaveRelations,
  onProductUpdated,
  products = [],
  showAddProductsModal: externalShowModal,
  setShowAddProductsModal: setExternalShowModal,
  stockLabel,
}) => {
  const [internalShowModal, setInternalShowModal] = useState(false);
  
  // Use external modal state if provided, otherwise use internal state
  const showModal = externalShowModal !== undefined ? externalShowModal : internalShowModal;
  const setShowModal = setExternalShowModal || setInternalShowModal;
  const [updatingProductId, setUpdatingProductId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  
  // Edit form fields
  const [editOrderQuantity, setEditOrderQuantity] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editSupplierPrice, setEditSupplierPrice] = useState("");
  const [editUnitCost, setEditUnitCost] = useState("");
  const [editContainerType, setEditContainerType] = useState("");
  const [editContainerQuantity, setEditContainerQuantity] = useState("");

  // Container type options (values for API); display labels are singular
  const containerTypeOptions = ["Individual", "Boxes", "Packages", "Cartons", "Cases", "Pallets", "Bags", "Bottles", "Cans"];
  const containerTypeDisplayLabel = { Individual: "Individual", Boxes: "Box", Packages: "Package", Cartons: "Carton", Cases: "Case", Pallets: "Pallet", Bags: "Bag", Bottles: "Bottle", Cans: "Can" };

  // Fetch units from API (matching updateItem.jsx)
  const fetchUnits = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_UNITS, {
        params: {
          SortBy: "MeasureName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "1000",
          PageNumber: 1,
        },
      });

      if (response.data.status) {
        const unitsData = response.data.data || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } else {
        setUnits([]);
      }
    } catch (err) {
      console.error("Error fetching units:", err);
      setUnits([]);
    }
  }, []);

  // Filter units based on measurement type (matching updateItem.jsx pattern)
  useEffect(() => {
    if (!editingProduct) {
      setFilteredUnits([]);
      return;
    }
    
    const measurementType = editingProduct.menuItem_MeasurementType || editingProduct.MeasuringType || editingProduct.measuringType;
   
    if (measurementType && units.length > 0) {
      // Filter units to match the measurement type exactly
      const filtered = units.filter(
        (unit) => {
          const unitMeasurementType = unit.MeasurementTypeName || unit.measurementTypeName;
          const matches = unitMeasurementType === measurementType;
          if (matches) {
            console.log("Unit matches:", unit.MeasureName || unit.measureName, "Type:", unitMeasurementType);
          }
          return matches;
        }
      );
      setFilteredUnits(filtered);
    } else {
      setFilteredUnits([]);
    }
  }, [editingProduct, units]);

  useEffect(() => {
    // Fetch units when component mounts
    fetchUnits();
  }, [fetchUnits]);

  // Also fetch units when edit modal opens if not already loaded
  useEffect(() => {
    if (showEditModal && units.length === 0) {
      fetchUnits();
    }
  }, [showEditModal, units.length, fetchUnits]);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const getDisplayName = (p) =>
    (p?.menuItem_DefaultVariantTitle ?? p?.MenuItem_DefaultVariantTitle ?? p?.menuItem_Name ?? p?.MenuItem_Name ?? "").toString();

  // Get unit conversion factor from API (Measurement represents how much of the base unit each unit equals)
  const getUnitConversionFactor = useCallback(async (unitName) => {
    if (!unitName || unitName.toLowerCase() === "units" || unitName.toLowerCase() === "unit") {
      return 1;
    }

    try {
      // Try to find unit in already fetched units
      const foundUnit = units.find(u => 
        (u.MeasureName || u.measureName || "").toLowerCase() === unitName.toLowerCase().trim()
      );
      
      if (foundUnit) {
        return foundUnit.Measurement || foundUnit.measurement || 1;
      }

      // If not found, try API call
      const response = await axiosInstance.get(APIs.GET_UNIT_BY_NAME || `/api/Unit/GetUnitByName`, {
        params: { unitName: unitName.trim() }
      });

      if (response.data && response.data.status && response.data.data) {
        const unit = response.data.data;
        return unit.Measurement || unit.measurement || 1;
      }
    } catch (error) {
      console.warn(`Failed to get conversion factor for unit "${unitName}":`, error);
    }

    // Fallback to common conversions if API fails
    const unitConversions = {
      'gram': 0.001, 'grams': 0.001, 'g': 0.001,
      'kilogram': 1, 'kilograms': 1, 'kg': 1,
      'milliliter': 0.001, 'milliliters': 0.001, 'ml': 0.001,
      'liter': 1, 'liters': 1, 'l': 1,
    };

    const lowerUnitName = unitName.toLowerCase();
    return unitConversions[lowerUnitName] || 1;
  }, [units]);

  // Calculate base unit quantity for an item
  const calculateBaseUnitQuantity = useCallback(async (item) => {
    const orderQuantity = parseFloat(item.orderQuantity || item.OrderQuantity || 0);
    const containerQuantity = parseFloat(item.containerQuantity || item.ContainerQuantity || 1);
    const containerType = item.containerType || item.ContainerType || "Individual";
    const unit = item.unit || item.Unit || "";
    const defaultSize = parseFloat(item.menuItem_DefaultSize || item.MenuItem_DefaultSize || 0);
    const defaultSizeUnit = item.menuItem_DefaultSizeUnit || item.MenuItem_DefaultSizeUnit || "";

    // Calculate total quantity in purchased units
    let totalQuantity;
    let unitForConversion = unit; // Default to the unit field
    
    if (containerType.toLowerCase() === "individual" && defaultSize > 0) {
      // For individual items with default size: multiply order quantity by default size
      // Example: 5 items × 200g default size = 1000g total
      totalQuantity = orderQuantity * defaultSize;
      // Use defaultSizeUnit for conversion since totalQuantity is in that unit
      unitForConversion = defaultSizeUnit || unit;
    } else {
      // For containered items or items without default size
      totalQuantity = (containerType.toLowerCase() === "individual" || containerQuantity <= 1)
        ? orderQuantity
        : orderQuantity * containerQuantity;
      // Use the unit field for conversion
      unitForConversion = unit;
    }

    // Get conversion factor (Measurement represents how much of the base unit each unit equals)
    const conversionFactor = await getUnitConversionFactor(unitForConversion);

    // Convert to base unit: total quantity * conversion factor
    // Example: Gram has Measurement = 0.001, so 1000 grams * 0.001 = 1 kg
    const baseUnitQuantity = conversionFactor > 0 ? totalQuantity * conversionFactor : totalQuantity;

    return baseUnitQuantity.toString();
  }, [getUnitConversionFactor]);

  // Handle modal submit - save relations to database
  const handleModalSubmit = async (newSelectedProducts) => {
    if (onSaveRelations) {
      // onSaveRelations handles setSelectedProducts internally, so we don't call it again
      const success = await onSaveRelations(newSelectedProducts);
      return success;
    } else {
      setSelectedProducts(newSelectedProducts);
      return true;
    }
  };

  // Remove product from database and local state
  const removeProduct = async (productId) => {
    const product = selectedProducts.find(p => p.menuItem_ID === productId);
    if (!product) return;

    if (!product.id || product.id === 0 || product.id === "0") {
      setSelectedProducts(selectedProducts.filter(p => p.menuItem_ID !== productId));
      return;
    }

    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to remove "${getDisplayName(product)}" from this purchase order?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setUpdatingProductId(productId);
      await axiosInstance.delete(`${APIs.DELETE_REMOVE_PRODUCT_FROM_PO}?productId=${product.id}&compId=${compID}`);
      const updatedProducts = selectedProducts.filter(p => p.menuItem_ID !== productId);
      setSelectedProducts(updatedProducts);
      if (onProductUpdated) {
        await onProductUpdated(updatedProducts);
      }
      showSuccessToast(`"${getDisplayName(product)}" removed successfully`);
    } catch (error) {
      console.error('Error deleting product:', error);
      showErrorToast(`Failed to remove "${getDisplayName(product)}"`);
    } finally {
      setUpdatingProductId(null);
    }
  };

  // Handle edit button click
  const handleEditClick = async (product) => {
    // Ensure units are fetched before opening modal
    if (units.length === 0) {
      await fetchUnits();
    }
    
    // Extract container type - check multiple possible property names
    let containerType = 
      product.ContainerType || 
      product.containerType || 
      product.MenuItem_PurchaseContainerType || 
      product.menuItem_PurchaseContainerType || 
      "Individual";
    
    // Ensure container type matches one of the dropdown options (case-insensitive)
    const normalizedContainerType = containerType.trim();
    const matchingOption = containerTypeOptions.find(
      option => option.toLowerCase() === normalizedContainerType.toLowerCase()
    );
    containerType = matchingOption || containerType || "Individual";
    
    // Extract container quantity - check multiple possible property names
    const containerQty = 
      product.ContainerQuantity || 
      product.containerQuantity || 
      product.MenuItem_PurchaseContainerQty || 
      product.menuItem_PurchaseContainerQty || 
      1;
    
  
    
    setEditingProduct(product);
    setEditOrderQuantity(product.orderQuantity?.toString() || "");
    setEditUnit(product.unit || product.menuItem_DefaultSizeUnit || product.MenuItem_DefaultSizeUnit || "");
    setEditSupplierPrice(product.supplierPrice?.toString() || "");
    setEditUnitCost(product.unitCost?.toString() || "");
    setEditContainerType(containerType);
    setEditContainerQuantity(containerQty?.toString() || "1");
    setShowEditModal(true);
  };

  // Handle edit modal submission
  const handleEditSubmit = async () => {
    if (!editingProduct || !compID || !poId) return;

    if (!editingProduct.id || editingProduct.id === 0 || editingProduct.id === "0") {
      showErrorToast(`Cannot update ${getDisplayName(editingProduct)} - please save changes first`);
      return;
    }

    if (!editOrderQuantity || !editSupplierPrice) {
      showErrorToast("Please fill in Order Quantity and Supplier Price");
      return;
    }

    try {
      setUpdatingProductId(editingProduct.menuItem_ID);
      // Get measuring type from editing product
      const measuringType = editingProduct.menuItem_MeasurementType || editingProduct.MeasuringType || editingProduct.measuringType || "Unit";
      
      // Calculate base unit quantity
      const productItem = {
        orderQuantity: parseFloat(editOrderQuantity || 0),
        containerType: editContainerType || "Individual",
        containerQuantity: editContainerQuantity ? parseInt(editContainerQuantity) : 1,
        unit: editUnit || "",
        menuItem_DefaultSize: editingProduct.menuItem_DefaultSize || editingProduct.MenuItem_DefaultSize,
        menuItem_DefaultSizeUnit: editingProduct.menuItem_DefaultSizeUnit || editingProduct.MenuItem_DefaultSizeUnit || ""
      };
      const baseUnitQuantity = await calculateBaseUnitQuantity(productItem);
      
      const updatePayload = {
        Id: editingProduct.id,
        PO_ID: parseInt(poId),
        MenuItem_ID: editingProduct.menuItem_ID,
        CompID: compID,
        MenuItem_Name: getDisplayName(editingProduct),
        ContainerType: editContainerType || "Individual",
        ContainerQuantity: editContainerQuantity ? parseInt(editContainerQuantity) : 1,
        MeasuringType: measuringType,
        OrderQuantity: parseFloat(editOrderQuantity || 0),
        Unit: editUnit || "",
        SupplierPrice: parseFloat(editSupplierPrice || 0),
        OrderQuantityInBaseUnit: baseUnitQuantity,
        UnitCost: editingProduct.unitCost ? parseFloat(editingProduct.unitCost) : null,
        MenuItem_PurchaseContainerType: editContainerType || "Individual",
        MenuItem_PurchaseContainerQty: editContainerQuantity ? parseInt(editContainerQuantity) : 1,
        MenuItem_DefaultSize: editingProduct.menuItem_DefaultSize || editingProduct.MenuItem_DefaultSize,
        MenuItem_DefaultSizeUnit: editingProduct.menuItem_DefaultSizeUnit || editingProduct.MenuItem_DefaultSizeUnit || ""
      };

      await axiosInstance.put(APIs.PUT_UPDATE_PO_PRODUCT, updatePayload);
      showSuccessToast(`${getDisplayName(editingProduct)} updated successfully`);
      
      const updatedProducts = selectedProducts.map(p =>
        p.menuItem_ID === editingProduct.menuItem_ID ? { 
          ...p, 
          orderQuantity: editOrderQuantity,
          unit: editUnit,
          supplierPrice: editSupplierPrice,
          unitCost: editingProduct.unitCost,
          containerType: editContainerType,
          containerQuantity: editContainerQuantity,
          menuItem_PurchaseContainerType: editContainerType,
          menuItem_PurchaseContainerQty: parseInt(editContainerQuantity) || 1
        } : p
      );
      setSelectedProducts(updatedProducts);
      if (onProductUpdated) {
        await onProductUpdated(updatedProducts);
      }
      
      setShowEditModal(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      showErrorToast(`Failed to update "${getDisplayName(editingProduct)}"`);
    } finally {
      setUpdatingProductId(null);
    }
  };

  // Render Edit Modal
  const renderEditModal = () => (
    <CustomModal
      isOpen={showEditModal}
      onClose={() => {
        setShowEditModal(false);
        setEditingProduct(null);
      }}
      title="Edit Purchase Order Item"
      subtitle={`Edit details for ${getDisplayName(editingProduct)}`}
      modalSize="modal-lg"
      showButton={true}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText="Submit"
      onSubmit={handleEditSubmit}
      submitButtonDisabled={
        !editingProduct ||
        editOrderQuantity === null ||
        editOrderQuantity === undefined ||
        String(editOrderQuantity).trim() === "" ||
        editSupplierPrice === null ||
        editSupplierPrice === undefined ||
        String(editSupplierPrice).trim() === "" ||
        updatingProductId === editingProduct?.menuItem_ID
      }
      isCentered={true}
    >
      {editingProduct && (
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput
              label="Menu Item Name"
              type="text"
              value={getDisplayName(editingProduct)}
              disabled
              placeHolder="Item name"
            />
          </div>
          {/* Hidden Container Type and Container Quantity fields - values are preserved but not shown */}
          <div className="col-md-6" style={{ display: "none" }}>
            <label className="form-label" style={{ fontWeight: "500", marginBottom: "8px", color: isDarkMode ? "#ffffff" : "#495057" }}>
              Container Type *
            </label>
            <select
              className="form-control"
              value={editContainerType}
              onChange={(e) => setEditContainerType(e.target.value)}
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                color: isDarkMode ? "#ffffff" : "#495057",
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #ced4da",
                borderRadius: "6px",
                padding: "8px 12px",
              }}
            >
              {containerTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {containerTypeDisplayLabel[type] ?? type}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6" style={{ display: "none" }}>
            <FormInput
              label="Container Quantity *"
              type="number"
              value={editContainerQuantity}
              onchange={(e) => setEditContainerQuantity(e.target.value)}
              placeHolder="Items per container"
              required
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label={`Order Quantity *${editContainerType && editContainerType !== "Individual" && editContainerQuantity ? ` (${editContainerType} × ${editContainerQuantity})` : ""}`}
              type="number"
              step="0.01"
              value={editOrderQuantity}
              onchange={(e) => setEditOrderQuantity(e.target.value)}
              placeHolder="0.00"
              required
            />
            {/* Show package size with unit for individual items */}
            {editContainerType === "Individual" && editingProduct?.menuItem_DefaultSize && (
              <div className="mt-2" style={{ 
                fontSize: "12px", 
                color: isDarkMode ? "#a0aec0" : "#6c757d",
                fontStyle: "italic"
              }}>
                <i className="fa fa-info-circle me-1"></i>
                Package size: {editingProduct.menuItem_DefaultSize} {editingProduct.menuItem_DefaultSizeUnit || editingProduct.unit || "units"}
              </div>
            )}
          </div>
          {/* Unit field - HIDDEN as per instructions */}
          {false && (
          <div className="col-md-6">
            <label className="form-label fw-medium mb-2" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
              Unit {(() => {
                const measurementType = editingProduct?.menuItem_MeasurementType || editingProduct?.MeasuringType || editingProduct?.measuringType;
                return measurementType && `(${measurementType})`;
              })()}
            </label>
            {(() => {
              const measurementType = editingProduct?.menuItem_MeasurementType || editingProduct?.MeasuringType || editingProduct?.measuringType;
              
              // If measurement type is Weight or Volume, always show dropdown with filtered units (like updateItem.jsx)
              if (measurementType && (measurementType === "Weight" || measurementType === "Volume")) {
                // If units are not loaded yet, show loading message
                if (units.length === 0) {
                  return (
                    <select
                      className="form-control"
                      disabled
                      style={{
                        borderRadius: "8px",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                        background: isDarkMode ? "#2d3436" : "#F9F9F9",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        padding: "8px 12px",
                      }}
                    >
                      <option value="">Loading units...</option>
                    </select>
                  );
                }
                
                // If units are loaded but none match the measurement type
                if (filteredUnits.length === 0) {
                  return (
                    <select
                      className="form-control"
                      value={editUnit}
                      onChange={(e) => setEditUnit(e.target.value)}
                      style={{
                        borderRadius: "8px",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                        background: isDarkMode ? "#2d3436" : "#F9F9F9",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        padding: "8px 12px",
                      }}
                    >
                      <option value="">No units found for {measurementType}</option>
                      <option value={editUnit}>{editUnit || "Manual entry"}</option>
                    </select>
                  );
                }
                
                // Show filtered units dropdown
                return (
                  <select
                    className="form-control"
                    value={editUnit}
                    onChange={(e) => setEditUnit(e.target.value)}
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="">Select unit</option>
                    {filteredUnits.map((unit) => {
                      const unitId = unit.MeasureID || unit.measureID || unit.unitID || unit.id;
                      const unitName = unit.MeasureName || unit.measureName || unit.unitName || unit.name;
                      const unitSymbol = unit.symbol || unit.Symbol || unitName;
                      return (
                        <option key={unitId} value={unitName}>
                          {unitSymbol}
                        </option>
                      );
                    })}
                  </select>
                );
              }
              
              // If measurement type is Unit or not set, show text input
              return (
                <FormInput
                  type="text"
                  value={editUnit}
                  onchange={(e) => setEditUnit(e.target.value)}
                  placeHolder="e.g., pcs, piece, unit"
                />
              );
            })()}
          </div>
          )}
          <div className="col-md-6">
            <FormInput
              label={(() => {
                let priceLabel = "Supplier Price *";
                if (editContainerType && editContainerType !== "Individual") {
                  // Convert plural container types to singular for "per box", "per package", etc.
                  const containerTypeMap = {
                    "Boxes": "box",
                    "Packages": "package",
                    "Cartons": "carton",
                    "Cases": "case",
                    "Pallets": "pallet",
                    "Bags": "bag",
                    "Bottles": "bottle",
                    "Cans": "can"
                  };
                  const singularType = containerTypeMap[editContainerType] || editContainerType.toLowerCase().slice(0, -1);
                  priceLabel = `Supplier Price * (per ${singularType})`;
                }
                else{
                  priceLabel = `Supplier Price * (per item)`;
                }
                return priceLabel;
              })()}
              type="number"
              step="0.01"
              value={editSupplierPrice}
              onchange={(e) => setEditSupplierPrice(e.target.value)}
              placeHolder="0.00"
              required
            />
          </div>
        </div>
      )}
    </CustomModal>
  );

  return (
    <>
      <div className="mt-3">
        {selectedProducts && selectedProducts.length > 0 ? (
          <>
            <div
              className="table-responsive table-container"
              style={{
                minHeight: "200px",
                maxHeight: "450px",
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
                zIndex: 1,
              }}
            >
              <table
                className="table table-hover"
                style={{ position: "relative" }}
              >
                <thead
                  className="bg-light sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr
                    style={{
                      borderBottom: `2px solid ${themeColor}`,
                    }}
                  >
                    <th className="pt-2 pb-2" style={{ fontWeight: "600" }}>Item Name</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Order Quantity</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Unit</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>{stockLabel || "Current Stock"}</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Supplier Price</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Total Supplier Price</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Unit Cost</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Total Cost</th>
                    <th className="pt-2 pb-2 text-center" style={{ fontWeight: "600" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map((product) => (
                    <tr
                      key={product.menuItem_ID}
                      draggable={false}
                    >
                      <td className="align-middle" style={{ padding: "12px" }}>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              background: isDarkMode ? "#4a5568" : "#f8f9fa",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "10px",
                              border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #e9ecef",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {product.menuItem_Image ? (
                              <LazyLoadImage
                                src={product.menuItem_Image}
                                alt={getDisplayName(product)}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <i className="fa fa-box" style={{ fontSize: "14px", color: isDarkMode ? "#a0aec0" : "#6c757d" }}></i>
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: "500" }}>{getDisplayName(product)}</div>
                            {product.containerType && (
                              <small style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}>
                                {product.containerType}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>{product.orderQuantity || "0"}</td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>
                        {(() => {
                          const measurementType = product.menuItem_MeasurementType ?? product.MenuItem_MeasurementType ?? product.MeasuringType ?? product.measuringType ?? "";
                          const defaultSizeUnit = product.menuItem_DefaultSizeUnit ?? product.MenuItem_DefaultSizeUnit ?? "";
                          const unit = product.unit || product.Unit || "";
                          const defaultSize = product.menuItem_DefaultSize ?? product.MenuItem_DefaultSize;
                          const containerType = product.containerType || product.ContainerType || "Individual";
                          
                          // For Individual items with default size, show "defaultSize unit"
                          if (containerType === "Individual" && defaultSize) {
                            const displayUnit = defaultSizeUnit || unit || "";
                            return (
                              <span>
                                {defaultSize} {displayUnit}
                              </span>
                            );
                          }
                          
                          // For other items, show the unit of measurement
                          let unitLabel = "";
                          if (measurementType && measurementType !== "Unit") {
                            // For Weight/Volume, prioritize defaultSizeUnit, then unit field, then fallback
                            unitLabel = defaultSizeUnit || unit || (measurementType === "Weight" ? "kg" : measurementType === "Volume" ? "L" : "");
                          } else {
                            // For Unit type, use unit field, then defaultSizeUnit
                            unitLabel = unit || defaultSizeUnit || "";
                          }
                          
                          return <span>{unitLabel}</span>;
                        })()}
                      </td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>
                        {(() => {
                          const currentQty = product.current_Quantity ?? product.Current_Quantity ?? 0;
                          const measurementType = product.menuItem_MeasurementType ?? product.MenuItem_MeasurementType ?? product.MeasuringType ?? product.measuringType ?? "";
                          const defaultSizeUnit = product.menuItem_DefaultSizeUnit ?? product.MenuItem_DefaultSizeUnit ?? "";
                          const unit = product.unit || product.Unit || "";
                          const defaultSize = parseFloat(product.menuItem_DefaultSize ?? product.MenuItem_DefaultSize ?? 1) || 1;
                          
                          // Format quantity - convert to display units if needed
                          let displayQty = parseFloat(currentQty) || 0;
                          
                          // Convert to display units if measurement type is Weight or Volume
                          if (measurementType && measurementType !== "Unit" && defaultSize > 0) {
                            displayQty = displayQty * defaultSize;
                          }
                          
                          // Get unit label - check all possible sources for unit
                          let unitLabel = "";
                          if (measurementType && measurementType !== "Unit") {
                            // For Weight/Volume, prioritize defaultSizeUnit, then fallback
                            unitLabel = defaultSizeUnit || unit || (measurementType === "Weight" ? "kg" : measurementType === "Volume" ? "L" : "");
                          } else {
                            // For Unit type or unknown, use unit field, then defaultSizeUnit, then empty
                            unitLabel = unit || defaultSizeUnit || "";
                          }
                          
                          return (
                            <span>
                              {displayQty.toFixed(2)}
                              {unitLabel && <span className="text-muted ms-1" style={{ fontSize: "0.9em" }}>{unitLabel}</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>${parseFloat(product.supplierPrice || 0).toFixed(2)}</td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>
                        {/* Use TotalSupplierPrice from database - already calculated correctly */}
                        ${parseFloat(product.totalSupplierPrice || product.TotalSupplierPrice || 0).toFixed(2)}
                      </td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>${parseFloat(product.unitCost || 0).toFixed(2)}</td>
                      <td className="align-middle text-center" style={{ padding: "12px" }}>
                        {/* Use TotalCost from database - already calculated correctly */}
                        ${parseFloat(product.totalCost || product.TotalCost || 0).toFixed(2)}
                      </td>

                      <td className="align-middle text-center" style={{ padding: "12px" }}>
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            onClick={() => handleEditClick(product)}
                            className="btn btn-sm btn-outline-primary"
                            disabled={updatingProductId === product.menuItem_ID}
                            title="Edit Item"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: `1px solid ${themeColor}`,
                              color: themeColor,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = themeColor;
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = themeColor;
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {updatingProductId === product.menuItem_ID ? (
                              <i className="fa fa-spinner fa-spin" style={{ fontSize: '12px' }}></i>
                            ) : (
                              <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                            )}
                          </button>
                          <button
                            onClick={() => removeProduct(product.menuItem_ID)}
                            className="btn btn-sm btn-outline-danger"
                            disabled={updatingProductId === product.menuItem_ID}
                            title="Delete Item"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc3545';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {updatingProductId === product.menuItem_ID ? (
                              <i className="fa fa-spinner fa-spin" style={{ fontSize: '12px' }}></i>
                            ) : (
                              <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Total Summary */}
            {(() => {
              const totalSupplierPrice = selectedProducts.reduce((sum, product) => {
                return sum + (parseFloat(product.totalSupplierPrice || product.TotalSupplierPrice || 0));
              }, 0);
              
              const totalCost = selectedProducts.reduce((sum, product) => {
                return sum + (parseFloat(product.totalCost || product.TotalCost || 0));
              }, 0);
              
              return (
                <div className="mt-4 pt-3" style={{
                  borderTop: isDarkMode ? "2px solid #404040" : "2px solid #e9ecef",
                }}>
                  <div className="row">
                    <div className="col-md-6 offset-md-6">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ 
                          fontWeight: "600", 
                          fontSize: "15px",
                          color: isDarkMode ? "#ffffff" : "#495057"
                        }}>
                          Total Supplier Price:
                        </span>
                        <span style={{ 
                          fontWeight: "700", 
                          fontSize: "16px",
                          color: themeColor
                        }}>
                          ${totalSupplierPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ 
                          fontWeight: "600", 
                          fontSize: "15px",
                          color: isDarkMode ? "#ffffff" : "#495057"
                        }}>
                          Total Cost:
                        </span>
                        <span style={{ 
                          fontWeight: "700", 
                          fontSize: "16px",
                          color: isDarkMode ? "#ffffff" : "#495057"
                        }}>
                          ${totalCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          <div className="text-center py-4">
            <div
              style={{
                width: "60px",
                height: "60px",
                background: isDarkMode ? "#555" : "#f8f9fa",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                border: isDarkMode ? "1px solid #666" : "1px solid #e9ecef"
              }}
            >
              <i className="fa fa-box" style={{ fontSize: "24px", color: isDarkMode ? "#ffffff" : "#6c757d" }}></i>
            </div>
            <p style={{ color: isDarkMode ? "#ffffff" : "#6c757d", fontSize: "15px", marginBottom: "20px" }}>
              No products added to this purchase order
            </p>
            <button
              onClick={() => setShowModal(true)}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: `1px solid ${themeColor}`,
                boxShadow: `0 4px 15px ${themeColor}40`,
                borderRadius: "8px",
                padding: "10px 20px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 6px 20px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 4px 15px ${themeColor}40`;
              }}
            >
              <i className="fa-solid fa-plus" style={{ fontSize: "12px" }}></i>
              Add Products
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {renderEditModal()}

      <ProductMultiSelectionModal
        selectedProducts={[]}
        setSelectedProducts={(newProducts) => {
          // Only update local state if onSaveRelations is not provided
          // When onSaveRelations is provided, the parent handles state management
          if (!onSaveRelations) {
            setSelectedProducts(newProducts.map((p) => {
              // Extract container type - check multiple possible property names from API
              const purchaseContainerType =
                p.MenuItem_PurchaseContainerType ||
                p.menuItem_PurchaseContainerType ||
                p.menuItem_PurchaseContainerType ||
                p.containerType ||
                p.ContainerType ||
                "Individual";

              // Extract container quantity - check multiple possible property names from API
              const purchaseContainerQty =
                p.MenuItem_PurchaseContainerQty ||
                p.menuItem_PurchaseContainerQty ||
                p.menuItem_PurchaseContainerQty ||
                p.containerQuantity ||
                p.ContainerQuantity ||
                1;

              // Extract measurement type
              const measuringType =
                p.MenuItem_MeasurementType ||
                p.menuItem_MeasurementType ||
                p.MeasuringType ||
                p.measuringType ||
                "Unit";

              return {
                ...p,
                orderQuantity: p.orderQuantity || "1",
                unit: p.unit || p.menuItem_DefaultSizeUnit || p.MenuItem_DefaultSizeUnit || "",
                supplierPrice: p.supplierPrice || "0",
                unitCost: p.unitCost || "0",
                containerType: purchaseContainerType,
                containerQuantity: purchaseContainerQty?.toString() || "1",
                menuItem_PurchaseContainerType: purchaseContainerType,
                menuItem_PurchaseContainerQty: typeof purchaseContainerQty === 'number' ? purchaseContainerQty : parseInt(purchaseContainerQty) || 1,
                menuItem_MeasurementType: measuringType,
                menuItem_DefaultSize: p.menuItem_DefaultSize || p.MenuItem_DefaultSize,
                menuItem_DefaultSizeUnit: p.menuItem_DefaultSizeUnit || p.MenuItem_DefaultSizeUnit || "",
                MeasuringType: measuringType,
              };
            }));
          }
        }}
        showModal={showModal}
        setShowModal={setShowModal}
        onSubmit={handleModalSubmit}
        products={(() => {
          // Filter out products that are already selected
          const selectedItemIds = (selectedProducts || []).map(p =>
            p.menuItem_ID || p.MenuItem_ID
          );
          const normalizeType = (v) => (v ?? "").toString().trim().toLowerCase();

          return (products || []).filter((product) => {
            const id = product.menuItem_ID || product.MenuItem_ID;
            if (selectedItemIds.includes(id)) return false;

            const type = normalizeType(product.menuItem_Type || product.MenuItem_Type);
            const hasVariants = !!(product.menuItem_HasVariant ?? product.MenuItem_HasVariant);

            // Skip "Product" rows that are variant groups (not purchasable)
            if (type === "product" && hasVariants) return false;

            // Skip combos from PO item selection
            if (type === "combo") return false;

            return true;
          });
        })()}
        showButton={true}
        stockLabel={stockLabel || "Stock"}
      />
    </>
  );
};

export default PurchaseOrderProductSelector;